import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "@phosphor-icons/react";
import Header from "@/components/Header";
import ContactFooter from "@/components/ContactFooter";
import { Reveal } from "@/components/Reveal";
import { MermaidDiagram } from "@/components/MermaidDiagram";
import { CALENDLY_URL } from "@/constants/site";
import { PATTERNS } from "@/constants/patterns";

const EASE = [0.16, 1, 0.3, 1];

export default function PatternStudyPage() {
  const { patternSlug } = useParams();
  const pattern = PATTERNS[patternSlug];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!pattern) return;
    document.title = pattern.seoTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", pattern.seoDescription);
    return () => {
      document.title = "Tech Executive Capability Center LLC";
    };
  }, [pattern]);

  if (!pattern) return <Navigate to="/#insights" replace />;

  return (
    <div className="min-h-screen bg-[#050505] text-white antialiased">
      <Header />
      <main data-testid="pattern-page" className="pt-32 lg:pt-40 pb-0">
        <div className="px-6 lg:px-12">
          <div className="max-w-[1440px] mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
            >
              <Link
                to="/#insights"
                data-testid="pattern-back-link"
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 hover:text-[#E6E4D9] transition-colors duration-300"
              >
                <ArrowLeft size={13} />
                All Insights
              </Link>
              <p className="mt-10 font-mono text-xs uppercase tracking-[0.3em] text-white/50">
                {pattern.overline}
              </p>
              <h1 className="mt-6 font-serif tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl leading-[1.05]">
                {pattern.title}{" "}
                <span className="italic text-[#E6E4D9]">{pattern.accent}</span>
              </h1>
              <p className="mt-8 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-white/60">
                {pattern.subtitle}
              </p>
              {pattern.thesis && (
                <p
                  data-testid="pattern-thesis"
                  className="mt-6 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-white/75"
                >
                  {pattern.thesis}
                </p>
              )}
            </motion.div>

            <Reveal>
              <h2 className="mt-20 font-mono text-xs uppercase tracking-[0.3em] text-white/50">
                {pattern.diagramTitle}
              </h2>
              <MermaidDiagram code={pattern.diagram} testId="pattern-diagram" />
            </Reveal>

            <Reveal>
              <h2 className="mt-8 font-mono text-xs uppercase tracking-[0.3em] text-white/50">
                {pattern.compareTitle}
              </h2>
              <div
                data-testid="pattern-compare-table"
                className="mt-8 border border-white/10 divide-y divide-white/10"
              >
                <div className="hidden lg:flex bg-white/[0.03]">
                  <div className="w-1/4 shrink-0 px-6 py-5" />
                  {pattern.columns.map((col, i) => (
                    <div
                      key={col.name}
                      className={`flex-1 px-6 py-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#E6E4D9] ${i > 0 ? "border-l border-white/10" : ""}`}
                    >
                      {col.name}
                      <span className="block mt-1 text-white/40">{col.note}</span>
                    </div>
                  ))}
                </div>
                {pattern.rows.map((row) => (
                  <div key={row.label} className="flex flex-col lg:flex-row">
                    <div className="lg:w-1/4 shrink-0 px-6 pt-6 lg:py-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">
                      {row.label}
                    </div>
                    {row.values.map((value, i) => (
                      <div
                        key={i}
                        className={`flex-1 px-6 pt-3 pb-5 lg:py-6 text-sm font-light leading-relaxed text-white/75 ${i > 0 ? "lg:border-l lg:border-white/10" : ""} ${i === row.values.length - 1 ? "pb-6 lg:pb-6" : ""}`}
                      >
                        <span className="lg:hidden block font-mono text-[9px] uppercase tracking-[0.2em] text-[#E6E4D9]/70 mb-2">
                          {pattern.columns[i].name}
                        </span>
                        {value}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                {pattern.columns.map((col) => (
                  <Link
                    key={col.slug}
                    to={`/insights/${col.slug}`}
                    data-testid={`pattern-blueprint-link-${col.slug}`}
                    className="group inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-[#E6E4D9] hover:border-[#E6E4D9]/50 transition-colors duration-300"
                  >
                    {col.linkLabel}
                    <ArrowUpRight size={12} weight="bold" />
                  </Link>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <h2 className="mt-24 font-mono text-xs uppercase tracking-[0.3em] text-white/50">
                {pattern.whyTitle}
              </h2>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                {pattern.whyCards.map((card) => (
                  <div key={card.title} className="bg-[#0A0A0A] p-8 lg:p-10">
                    <h3 className="font-serif text-xl sm:text-2xl text-white tracking-tight mb-4">
                      {card.title}
                    </h3>
                    <p className="text-sm font-light leading-relaxed text-white/60">
                      {card.body}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>

            {pattern.disclaimer && (
              <Reveal>
                <div
                  data-testid="pattern-disclaimer"
                  className="mt-16 border border-[#E6E4D9]/25 bg-[#E6E4D9]/[0.03] p-8 lg:p-10"
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E6E4D9] mb-4">
                    What CDC alone does not do
                  </p>
                  <p className="text-sm sm:text-base font-light leading-relaxed text-white/70 max-w-3xl">
                    {pattern.disclaimer}
                  </p>
                </div>
              </Reveal>
            )}

            {pattern.checklists && (
              <Reveal>
                <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
                  {pattern.checklists.map((list) => (
                    <div
                      key={list.title}
                      data-testid={list.testId}
                      className="bg-[#0A0A0A] p-8 lg:p-10"
                    >
                      <h3 className="font-serif text-xl sm:text-2xl text-white tracking-tight mb-6">
                        {list.title}
                      </h3>
                      <ul className="space-y-4">
                        {list.items.map((item, j) => (
                          <li
                            key={j}
                            className="flex gap-4 text-sm font-light leading-relaxed text-white/70"
                          >
                            <span className="mt-[0.6rem] h-px w-5 shrink-0 bg-[#E6E4D9]/50" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}

            <Reveal>
              <div
                data-testid="pattern-cta"
                className="mt-24 mb-4 border border-white/10 bg-white/[0.02] px-6 lg:px-12 py-12 lg:py-16 text-center"
              >
                <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight max-w-2xl mx-auto leading-snug">
                  {pattern.cta}{" "}
                  <span className="italic text-[#E6E4D9]">let's sit the room.</span>
                </h3>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pattern-cta-button"
                  className="group mt-10 inline-flex items-center gap-3 bg-[#E6E4D9] text-black font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 hover:bg-[#C2C0B6] transition-colors duration-300"
                >
                  Schedule Executive Briefing
                  <ArrowUpRight
                    size={16}
                    weight="bold"
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
        <ContactFooter />
      </main>
    </div>
  );
}
